import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Briefcase, Sparkles, Heart, GraduationCap, Globe } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";

const OPEN_ROLES = [
  {
    title: "Senior Full-Stack Engineer",
    team: "Engineering",
    location: "Remote · Global",
    type: "Full-time",
    summary: "Lead end-to-end product builds on React/Next.js and Python/Node. You'll own technical decisions on engagements and mentor 2-3 mid-level engineers.",
  },
  {
    title: "AI / ML Engineer",
    team: "AI Practice",
    location: "Remote · NA + EU",
    type: "Full-time",
    summary: "Ship production LLM applications — RAG pipelines, agentic workflows, evaluation harnesses. Comfort with FastAPI, vector DBs, and prompt engineering required.",
  },
  {
    title: "Staff Product Designer",
    team: "Design",
    location: "Remote · Global",
    type: "Full-time",
    summary: "Shape product experiences from research to high-fidelity. You'll own the design system and partner directly with founders at our client startups.",
  },
  {
    title: "DevOps Engineer",
    team: "Cloud & Infra",
    location: "Remote · Global",
    type: "Full-time",
    summary: "Kubernetes, Terraform, multi-cloud, and FinOps. You'll architect production environments and lead incident response across client deployments.",
  },
  {
    title: "Engineering Manager",
    team: "Engineering",
    location: "NYC or Remote",
    type: "Full-time",
    summary: "Lead a pod of 6-8 engineers. Coach growth, run delivery, and partner with engagement leads on staffing and scope.",
  },
  {
    title: "iOS Engineer",
    team: "Mobile",
    location: "Remote · Global",
    type: "Contract → Full-time",
    summary: "Swift, SwiftUI, and a track record of shipped apps. Bonus points for Combine and a passion for shipping fast without breaking things.",
  },
];

const PERKS = [
  { icon: Globe, title: "Fully remote", body: "Work from anywhere. Optional hubs in NYC, Lisbon, and Bengaluru." },
  { icon: Sparkles, title: "Unlimited PTO", body: "Take what you need. We track who hasn't taken enough, not who has." },
  { icon: GraduationCap, title: "Learning budget", body: "$2,500/yr for books, conferences, courses, and craft." },
  { icon: Heart, title: "Health & wellness", body: "Premium health coverage and a wellness stipend everywhere we operate." },
  { icon: Briefcase, title: "Equipment", body: "Top-of-line laptop, monitor, and home-office stipend on day one." },
  { icon: MapPin, title: "Retreats", body: "Twice-yearly company retreats in beautiful places. Last one was Lisbon." },
];

export default function Careers() {
  return (
    <div data-testid="careers-page" className="pt-32 pb-20">
      <section className="mx-auto max-w-7xl px-6 md:px-12 mesh-bg pb-16">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">
            <Link to="/" className="hover:text-foreground">Home</Link> · Careers
          </p>
          <h1 className="mt-5 font-display text-5xl md:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Build the future of software with <span className="text-gradient">us</span>.
          </h1>
          <p className="mt-7 text-xl text-zinc-400 max-w-2xl">
            We're hiring kind, curious, deeply skilled people. If that sounds like you, we'd love to talk.
          </p>
        </Reveal>
      </section>

      <section data-testid="careers-perks" className="mx-auto max-w-7xl px-6 md:px-12 mt-20">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Why us ]</p>
          <h2 className="mt-5 font-display text-3xl md:text-5xl tracking-tight">Designed for craftspeople.</h2>
        </Reveal>
        <Stagger className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PERKS.map((p) => {
            const Icon = p.icon;
            return (
              <Item key={p.title}>
                <div className="rounded-2xl glass border-white/[0.08] p-7 h-full hover:bg-white/[0.04] transition">
                  <div className="h-10 w-10 grid place-items-center rounded-lg bg-white/5 border border-white/10">
                    <Icon className="h-4 w-4 text-nexus-cyan" />
                  </div>
                  <h3 className="mt-5 font-display text-xl tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{p.body}</p>
                </div>
              </Item>
            );
          })}
        </Stagger>
      </section>

      <section data-testid="careers-roles" className="mx-auto max-w-7xl px-6 md:px-12 mt-24">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-nexus-cyan font-bold">[ Open roles ]</p>
          <h2 className="mt-5 font-display text-3xl md:text-5xl tracking-tight">Currently hiring.</h2>
        </Reveal>
        <Stagger className="mt-12 grid gap-3">
          {OPEN_ROLES.map((r) => (
            <Item key={r.title}>
              <article
                data-testid={`role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group rounded-2xl glass border-white/[0.08] p-6 md:p-7 hover:bg-white/[0.04] transition"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="rounded-full bg-white/5 border border-white/10 text-[10px] uppercase tracking-widest text-zinc-300 px-3 py-1">
                        {r.team}
                      </span>
                      <span className="rounded-full bg-nexus-cyan/10 border border-nexus-cyan/30 text-[10px] uppercase tracking-widest text-nexus-cyan px-3 py-1">
                        {r.type}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                        <MapPin className="h-3 w-3" /> {r.location}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">{r.title}</h3>
                    <p className="mt-2 text-zinc-400 text-sm md:text-base leading-relaxed max-w-3xl">{r.summary}</p>
                  </div>
                  <a
                    href={`mailto:admin@fullstackendeavours.in?subject=Application: ${encodeURIComponent(r.title)}`}
                    data-testid={`role-apply-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-nexus-cyan text-nexus-ink font-semibold px-5 py-2.5 text-sm hover:brightness-110 transition"
                  >
                    Apply <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            </Item>
          ))}
        </Stagger>
      </section>

      <section className="mx-auto max-w-5xl px-6 md:px-12 mt-24">
        <div className="rounded-[2rem] glass border-white/[0.08] p-10 md:p-16 text-center">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">
            Don't see a perfect fit?
          </h2>
          <p className="mt-3 text-zinc-400 max-w-lg mx-auto">
            We hire exceptional people even when there's no open role. Send your portfolio and a note about what you'd want to do.
          </p>
          <a
            href="mailto:admin@fullstackendeavours.in"
            data-testid="careers-general-apply"
            className="mt-7 inline-flex items-center gap-2 rounded-full glass border-white/10 px-6 py-3 text-sm hover:bg-white/[0.06] transition"
          >
            admin@fullstackendeavours.in <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
